import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Cloud, CheckCircle2, AlertTriangle, Loader2, Eye, EyeOff, Trash2, Lightbulb, Sparkles } from "lucide-react";

/**
 * One-screen wizard to configure Backblaze B2 (S3-compatible) storage.
 *
 * UX model:
 *  - A tenant with no override transparently uses the platform-default bucket.
 *    We show a read-only "Default settings" panel and a checkbox the owner
 *    can uncheck to switch to a custom bucket.
 *  - Unchecking the box reveals the full validation form (the pre-existing flow).
 */
export default function StorageWizard() {
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ key_id: "", app_key: "", endpoint: "https://s3.us-east-005.backblazeb2.com", bucket: "", region: "us-east-005" });
  const [showSecret, setShowSecret] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  // null while we fetch /storage; once loaded we initialize from `current.custom`
  const [useDefaults, setUseDefaults] = useState(null);
  const [switching, setSwitching] = useState(false);
  const debounceRef = useRef(null);
  const seqRef = useRef(0);

  const load = async () => {
    try {
      const { data } = await api.get("/storage");
      setCurrent(data);
      // Platform Owner's config IS the default — always show the form.
      // Everyone else: show "Use defaults" when they have no custom override.
      setUseDefaults(data.is_platform_owner ? false : !data.custom);
    } catch {
      setCurrent({ configured: false, custom: false, using_defaults: false, defaults_available: false, is_platform_owner: false });
      setUseDefaults(true);
    }
  };
  useEffect(() => { load(); }, []);

  const canProbe = form.key_id.trim() && form.app_key.trim() && form.endpoint.trim();
  const bucketReady = !!form.bucket.trim();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (useDefaults || !canProbe) { setValidation(null); return; }
    debounceRef.current = setTimeout(() => doValidate(), 500);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [form.key_id, form.app_key, form.endpoint, form.bucket, form.region, useDefaults]);

  const doValidate = async () => {
    const seq = ++seqRef.current;
    setValidating(true);
    try {
      const { data } = await api.post("/storage/validate", form);
      if (seq !== seqRef.current) return;
      setValidation(data);
    } catch (err) {
      if (seq !== seqRef.current) return;
      setValidation({ ok: false, code: "network", message: fmtErr(err.response?.data?.detail) || "Validation failed" });
    } finally {
      if (seq === seqRef.current) setValidating(false);
    }
  };

  const save = async () => {
    if (!validation?.ok) { toast.error("Fix the validation errors before saving."); return; }
    if (!bucketReady) { toast.error("Bucket name is required before saving."); return; }
    setSaving(true);
    try {
      await api.post("/storage", form);
      toast.success("Storage configured — new recordings will upload here.");
      setForm({ ...form, app_key: "" });
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  // Called when the recruiter re-checks "Use default settings" after having a
  // custom config saved — we drop the override on the backend so the
  // env-level defaults take over again.
  const switchToDefaults = async () => {
    if (!window.confirm("Switch back to the platform's default recording storage? New recordings will be stored there going forward.")) return;
    setSwitching(true);
    try {
      await api.delete("/storage");
      toast.success("Switched to default storage.");
      setForm({ key_id: "", app_key: "", endpoint: "https://s3.us-east-005.backblazeb2.com", bucket: "", region: "us-east-005" });
      setValidation(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Could not switch back");
      // Re-sync UI to true server state on failure.
      load();
    } finally { setSwitching(false); }
  };

  // Checkbox handler: toggling OFF just hides the default panel and reveals
  // the form. Toggling ON when a custom config is saved triggers the DELETE.
  const onToggleDefaults = (next) => {
    if (next === false) { setUseDefaults(false); return; }
    if (current?.custom) { switchToDefaults(); return; }
    setUseDefaults(true);
  };

  const hint = useMemo(() => {
    if (!validation || validation.ok) return null;
    if (validation.code === "invalid_key_id") return "Tip: Backblaze's S3 API only accepts non-master App Keys. Create one via Backblaze → App Keys → Add a New Application Key.";
    if (validation.code === "bad_app_key") return "Secret doesn't match this Key ID — regenerate the App Key and paste it fresh.";
    if (validation.code === "bad_endpoint") return "Endpoint URL is wrong. Example: `https://s3.us-east-005.backblazeb2.com`.";
    if (validation.code === "bucket_not_visible") return "The App Key can see Backblaze but not this bucket. The key may be restricted to a different bucket, or the bucket name is misspelt.";
    return null;
  }, [validation]);

  // Loading guard — avoids a 1-frame flash of the custom form before we know
  // which panel to show.
  if (useDefaults === null) {
    return (
      <div className="tp-card p-6" data-testid="storage-wizard-loading">
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin"/> Loading storage settings…</div>
      </div>
    );
  }

  return (
    <div className="tp-card p-6" data-testid="storage-wizard">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Cloud className="w-5 h-5 text-indigo-500"/>
            <div className="font-display text-lg font-semibold">Recording Storage</div>
            {current?.is_platform_owner && <span className="tp-chip tp-chip-amber" data-testid="storage-status-po"><Sparkles className="w-3 h-3 inline mr-1"/>Platform default</span>}
            {!current?.is_platform_owner && current?.using_defaults && <span className="tp-chip tp-chip-indigo" data-testid="storage-status-default"><Sparkles className="w-3 h-3 inline mr-1"/>Using defaults</span>}
            {current?.custom && !current?.is_platform_owner && <span className="tp-chip tp-chip-emerald" data-testid="storage-status-ok"><CheckCircle2 className="w-3 h-3 inline mr-1"/>Custom</span>}
            {current && !current.configured && !current?.is_platform_owner && <span className="tp-chip tp-chip-amber" data-testid="storage-status-missing"><AlertTriangle className="w-3 h-3 inline mr-1"/>Not configured</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {current?.is_platform_owner
              ? "The bucket you save here becomes the default recording destination for every tenant that hasn't configured their own."
              : "Candidate video + coding recordings stream here as soon as they finish. New workspaces start on the platform default — opt out below to point at your own Backblaze B2 bucket."}
          </div>
        </div>
      </div>

      {/* Default-vs-custom toggle (hidden for Platform Owner — their config IS the default) */}
      {!current?.is_platform_owner && (
        <label className="flex items-start gap-3 p-3 mb-4 rounded-lg bg-indigo-50 border border-indigo-200 cursor-pointer" data-testid="storage-use-defaults-row">
          <input
            type="checkbox"
            data-testid="storage-use-defaults"
            checked={useDefaults}
            disabled={switching}
            onChange={(e) => onToggleDefaults(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-indigo-600"
          />
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
              Use default settings
              {switching && <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-500"/>}
            </div>
            <div className="text-xs text-slate-600">
              Keep recordings in the platform's managed bucket{current?.defaults_source === "platform_owner" ? " (set by your Platform Owner)" : ""}. Uncheck to configure a custom Backblaze B2 destination.
            </div>
          </div>
        </label>
      )}

      {useDefaults && !current?.is_platform_owner ? (
        <div className="p-4 rounded-lg bg-white border border-slate-200 text-xs space-y-1" data-testid="storage-defaults-panel">
          {current?.defaults_available ? (
            <>
              <div><span className="text-slate-500 uppercase tracking-wider mr-2">Bucket</span><span className="font-mono text-slate-800">{current.default_bucket}</span></div>
              <div><span className="text-slate-500 uppercase tracking-wider mr-2">Endpoint</span><span className="font-mono text-slate-800">{current.default_endpoint}</span></div>
              {current.default_region && <div><span className="text-slate-500 uppercase tracking-wider mr-2">Region</span><span className="font-mono text-slate-800">{current.default_region}</span></div>}
              <div className="text-[11px] text-slate-500 pt-1">All new video &amp; coding recordings for this workspace upload here.</div>
            </>
          ) : (
            <div className="flex items-start gap-2 text-amber-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0"/>
              <div>
                <div className="font-medium">Platform default storage isn't configured yet.</div>
                <div className="text-[11px] text-amber-700/80 mt-0.5">Ask your Emergent admin to set <code>B2_*</code> env vars, or uncheck above to add a custom bucket now.</div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          {current?.custom && (
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 space-y-0.5" data-testid="current-storage">
              <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">Currently saved</div>
              <div><span className="text-slate-500 uppercase tracking-wider mr-2">Key ID</span>{current.key_id_masked}</div>
              <div><span className="text-slate-500 uppercase tracking-wider mr-2">Bucket</span>{current.bucket}</div>
              <div><span className="text-slate-500 uppercase tracking-wider mr-2">Endpoint</span>{current.endpoint}</div>
              {current.last_validated_at && <div><span className="text-slate-500 uppercase tracking-wider mr-2">Validated</span>{new Date(current.last_validated_at).toLocaleString()}</div>}
            </div>
          )}
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">S3 Application Key ID</label>
            <input
              data-testid="storage-key-id"
              value={form.key_id}
              onChange={(e)=>setForm({...form, key_id: e.target.value.trim()})}
              placeholder="005abcdef0123456789abcdef"
              className={`w-full mt-1 px-3 py-2 rounded-lg bg-white border font-mono text-sm outline-none ${
                validation?.ok ? "border-emerald-500/50" :
                validation && !validation.ok ? "border-rose-500/50" :
                "border-slate-300 focus:border-indigo-500"
              }`}
            />
            <div className="text-[11px] text-slate-500 mt-1 flex items-start gap-1.5">
              <Lightbulb className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0"/>
              <span>Must be a <strong>non-master</strong> App Key with <strong>Read &amp; Write</strong> access.</span>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Application Key (secret)</label>
            <div className="relative mt-1">
              <input
                data-testid="storage-app-key"
                type={showSecret ? "text" : "password"}
                value={form.app_key}
                onChange={(e)=>setForm({...form, app_key: e.target.value.trim()})}
                placeholder="K005…"
                className="w-full px-3 py-2 pr-10 rounded-lg bg-white border border-slate-300 focus:border-indigo-500 outline-none font-mono text-sm"
              />
              <button type="button" onClick={() => setShowSecret(s => !s)} data-testid="toggle-secret" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">
                {showSecret ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider">Endpoint URL</label>
              <input
                data-testid="storage-endpoint"
                value={form.endpoint}
                onChange={(e)=>setForm({...form, endpoint: e.target.value.trim()})}
                placeholder="https://s3.us-east-005.backblazeb2.com"
                className="w-full mt-1 px-3 py-2 rounded-lg bg-white border border-slate-300 focus:border-indigo-500 outline-none font-mono text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider">Region</label>
              <input
                data-testid="storage-region"
                value={form.region}
                onChange={(e)=>setForm({...form, region: e.target.value.trim()})}
                placeholder="us-east-005"
                className="w-full mt-1 px-3 py-2 rounded-lg bg-white border border-slate-300 focus:border-indigo-500 outline-none font-mono text-sm"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider">Bucket name</label>
            <input
              data-testid="storage-bucket"
              value={form.bucket}
              onChange={(e)=>setForm({...form, bucket: e.target.value.trim()})}
              placeholder="recruitsift-recordings"
              list="available-buckets"
              className="w-full mt-1 px-3 py-2 rounded-lg bg-white border border-slate-300 focus:border-indigo-500 outline-none font-mono text-sm"
            />
            {validation?.available_buckets?.length > 0 && (
              <datalist id="available-buckets">
                {validation.available_buckets.map(b => <option key={b} value={b} />)}
              </datalist>
            )}
            {validation?.available_buckets?.length > 0 && (
              <div className="text-[11px] text-slate-500 mt-1" data-testid="available-buckets-hint">
                Buckets this key can see: {validation.available_buckets.map(b => (
                  <button key={b} type="button" onClick={() => setForm(f => ({...f, bucket: b}))} className="font-mono underline decoration-dotted hover:text-indigo-600 mr-2">{b}</button>
                ))}
              </div>
            )}
          </div>

          <div data-testid="validation-banner" aria-live="polite">
            {validating && (
              <div className="flex items-center gap-2 text-xs text-slate-500 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <Loader2 className="w-3.5 h-3.5 animate-spin"/> Validating credentials…
              </div>
            )}
            {!validating && validation?.ok && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                <div>
                  <div className="font-medium">{validation.message}</div>
                  {validation.available_buckets?.length > 0 && (
                    <div className="text-xs text-emerald-700/80 mt-0.5">{validation.available_buckets.length} bucket{validation.available_buckets.length === 1 ? "" : "s"} visible to this key.</div>
                  )}
                </div>
              </div>
            )}
            {!validating && validation && !validation.ok && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                <div>
                  <div className="font-medium">{validation.message}</div>
                  {hint && <div className="text-xs text-rose-700/80 mt-1">{hint}</div>}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid="storage-save"
              onClick={save}
              disabled={!validation?.ok || !bucketReady || saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
              Save custom bucket
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
